import { ContextualMenuItemType, IColumn, IContextualMenuItem } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import AddEditRemoveButtons from 'components/Buttons/AddEditRemoveButtons';
import UserRoleGroupPicker, {
  getUserRoleGroupItem,
  renderUserRoleGroupItem,
} from 'components/Pickers/UserRoleGroupPicker';
import BaseTableList from 'components/TableList/BaseTableList';
import ValidationIcon from 'components/Utils/IconsAndImages/ValidationIcon';
import { plannerIcon } from 'globalStyles';
import { Audience } from 'models/audience/audience';
import { AuthSchema } from 'models/auth/authSchema';
import { AuthSchemaLine } from 'models/auth/authSchemaLine';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetAudiences } from 'services/Api/audienceService';
import { apiRequest } from 'services/Auth/authConfig';
import { getRandomId } from 'utils/string';

interface IAuthSchemaLineEditorProps {
  authSchema: AuthSchema;
  readonly?: boolean;
  onUpdate?: (lines: AuthSchemaLine[]) => void;
}

export const AuthSchemaLineEditor = (props: IAuthSchemaLineEditorProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'adminAuth']);
  const [selectedLines, setSelectedLines] = useState<AuthSchemaLine[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState<string>(getRandomId());
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [audiencesLoading, setAudiencesLoading] = useState<boolean>(false);

  //
  // Columns
  //
  const getColumns = (): IColumn[] => {
    return [
      {
        key: 'member',
        name: t('adminAuth:TabPermissions.LineEditor.Columns.Member'),
        minWidth: 200,
        isResizable: true,
        isMultiline: true,
      },
      {
        key: 'op_read',
        name: t('adminAuth:TabPermissions.LineEditor.Columns.OpRead'),
        minWidth: 80,
        maxWidth: 80,
      },
      {
        key: 'op_update',
        name: t('adminAuth:TabPermissions.LineEditor.Columns.OpUpdate'),
        minWidth: 80,
        maxWidth: 80,
      },
      {
        key: 'op_delete',
        name: t('adminAuth:TabPermissions.LineEditor.Columns.OpDelete'),
        minWidth: 80,
        maxWidth: 80,
      },
    ];
  };

  const [columns, setColumns] = useState<IColumn[]>(getColumns());

  const onRenderItemColumn = (
    item?: AuthSchemaLine,
    column?: IColumn | undefined,
    index?: number | undefined,
  ): JSX.Element | null => {
    if (!item || !column) return null;

    switch (column.key) {
      case 'member':
        const member = item.getMember(appContext);
        const groupIconName =
          props.authSchema.bsEnabled && item.groupId === props.authSchema.bsGroupId ? plannerIcon.iconName : undefined;

        if (props.readonly && member) {
          //render readonly item
          const item = getUserRoleGroupItem(member);

          return renderUserRoleGroupItem(item, true, undefined, true, undefined, undefined, undefined, groupIconName);
        } else {
          return (
            <UserRoleGroupPicker
              allowedItems={[]}
              itemsToExclude={props.authSchema.lines.map((l) => l.getMember(appContext))}
              selectedItems={member ? [member] : []}
              onSelect={(items) => {
                if (items.length === 0) {
                  item.setMember(undefined);
                } else {
                  item.setMember(items[0]);
                }
                onUpdate(item);
              }}
              itemLimit={1}
              onRemove={() => {
                item.setMember(undefined);
                onUpdate(item);
              }}
              allowUsers={true}
              allowGroups={true}
              allowRoles={true}
              groupIcon={groupIconName}
            />
          );
        }
      case 'op_read':
        if (props.readonly) {
          return <ValidationIcon status={true} />;
        } else {
          return <IPCheckbox checked={true} disabled={true} />;
        }
      case 'op_update':
        if (props.readonly) {
          return <ValidationIcon status={item.opUpdate} />;
        } else {
          return (
            <IPCheckbox
              checked={item.opUpdate}
              onChange={(ev, checked) => {
                item.opUpdate = checked ?? false;
                onUpdate(item);
              }}
            />
          );
        }
      case 'op_delete':
        if (props.readonly) {
          return <ValidationIcon status={item.opDelete} />;
        } else {
          return (
            <IPCheckbox
              checked={item.opDelete}
              onChange={(ev, checked) => {
                item.opDelete = checked ?? false;
                onUpdate(item);
              }}
            />
          );
        }
    }

    return null;
  };

  //
  // CRUD
  //
  const onAdd = () => {
    if (!props.onUpdate) return;
    const newLine = new AuthSchemaLine();
    props.onUpdate([newLine, ...props.authSchema.lines]);
  };

  const onUpdate = (line: AuthSchemaLine) => {
    if (!props.onUpdate) return;
    props.onUpdate(props.authSchema.lines.map((l) => (l.id === line.id ? line.clone() : l)));
  };

  const onRemove = (lines: AuthSchemaLine[]) => {
    if (!props.onUpdate) return;
    props.onUpdate(props.authSchema.lines.filter((l) => !lines.some((line) => line.id === l.id)));
    setSelectionResetKey(getRandomId());
  };

  //
  // Load from audiences
  //
  const loadAudiences = async () => {
    try {
      setAudiencesLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newAudiences = await apiGetAudiences(accessToken, appContext.globalDataCache);
      newAudiences.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      setAudiences(newAudiences);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setAudiencesLoading(false);
    }
  };

  const getAddAudienceMenuOptions = (): IContextualMenuItem[] => {
    const items: IContextualMenuItem[] = [
      {
        key: 'h1',
        text: t('adminAuth:TabPermissions.LineEditor.AddFromAudiences'),
        itemType: ContextualMenuItemType.Header,
      },
    ];

    audiences.forEach((audience) => {
      items.push({
        key: audience.audienceId.toString(),
        text: audience.name,
        onClick: () => {
          addFromAudience(audience);
        },
      });
    });

    if (items.length === 1) {
      if (audiencesLoading) {
        items.push({
          key: 'loading',
          text: t('translation:General.Notifications.Loading'),
          disabled: true,
        });
      } else {
        items.push({
          key: 'noitems',
          text: t('adminAuth:TabPermissions.LineEditor.NoAudiencesAvailable'),
          disabled: true,
        });
      }
    }

    return items;
  };

  const addFromAudience = async (audience: Audience) => {
    try {
      if (!props.onUpdate) return;
      const newAuthSchema = props.authSchema.clone();

      for (let idx = 0; idx < audience.lines.length; idx++) {
        const line = audience.lines[idx];
        if (!newAuthSchema.hasMember(line.userId ?? line.groupId ?? line.roleId)) {
          const newLine = new AuthSchemaLine();
          newLine.userId = line.userId;
          newLine.groupId = line.groupId;
          newLine.roleId = line.roleId;
          newAuthSchema.lines.push(newLine);
        }
      }

      props.onUpdate(newAuthSchema.lines);
    } catch (err) {
      appContext.setError(err);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {!props.readonly && (
        <IPStack.Item>
          <IPLabel>{t('adminAuth:TabPermissions.LineEditor.Label')}</IPLabel>
        </IPStack.Item>
      )}
      {!props.readonly && (
        <IPStack.Item>
          <AddEditRemoveButtons
            onClickNew={onAdd}
            onNewItemsLoad={() => loadAudiences()}
            newItems={getAddAudienceMenuOptions()}
            onClickRemove={() => onRemove(selectedLines)}
            removeDisabled={selectedLines.length === 0}
            noLeftPadding
          />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <BaseTableList
          items={props.authSchema.lines}
          compact={props.readonly}
          columns={columns}
          selectionMode={props.readonly ? IPSelectionMode.none : IPSelectionMode.multiple}
          layoutMode={IPDetailsListLayoutMode.justified}
          onRenderCol={onRenderItemColumn}
          setColumns={setColumns}
          disableSort={true}
          selectionResetKey={selectionResetKey}
          onSelect={setSelectedLines}
          disableEmptyState={true}
        />
      </IPStack.Item>
    </IPStack>
  );
};
